import { useNavigate } from 'react-router-dom';
import { useBillingUsage, usePaymentMethods, useAutoRefillSettings } from '@/hooks/useBilling';
import { useUserSubscriptionStatus } from '@/hooks/useSubscriptions';
import { useUserType } from '@/hooks/useUserType';
import { CreditCard, Zap, AlertTriangle, Plus, Crown, RefreshCw } from 'lucide-react';
import { StatusBanner } from './StatusBanner';

export function PersistentAccountStatusBanner() {
  const navigate = useNavigate();
  const { data: billingUsage } = useBillingUsage();
  const { data: paymentMethods, isLoading: paymentMethodsLoading } = usePaymentMethods();
  const { data: subscriptionStatus } = useUserSubscriptionStatus();
  const { data: userType } = useUserType();
  const { data: autoRefillSettings, isSuccess: autoRefillLoaded } = useAutoRefillSettings();

  // Don't show anything while loading
  if (!billingUsage || paymentMethodsLoading) {
    return null;
  }

  const hasPaymentMethod = paymentMethods?.savedMethods && paymentMethods.savedMethods.length > 0;
  const isDelinquent = billingUsage.isDelinquent;
  const isLowCredits = billingUsage.creditsRemaining < 10 && billingUsage.creditsRemaining > 0;
  // Auto-refill is opt-in (never enabled automatically — it charges the card on
  // file); surface it exactly when the user is feeling the pain it prevents.
  // Only claim it's off once the query resolved — loading/error must not show
  // a "turn it on" CTA to someone who already has it on.
  const autoRefillOff = autoRefillLoaded && !autoRefillSettings?.enabled;
  // Only show "Plan Assigned" banner for partner_client users (sub-accounts)
  // Regular users should not see this banner even if they have an unpaid plan assignment
  const isPartnerClient = userType === 'partner_client';
  const hasAssignedPlan = isPartnerClient && subscriptionStatus?.needs_stripe_subscription && subscriptionStatus?.has_plan_assignment;

  // Priority hierarchy:
  // 1. Assigned plan needs activation (partner assigned a plan)
  // 2. No payment method (blocks everything)
  // 3. Delinquent account (service stopped)
  // 4. Low credits (warning)

  
  // Priority 1: Assigned plan needs activation (partner assigned a plan but user hasn't paid)
  if (hasAssignedPlan) {
    return (
      <StatusBanner
        variant="purple"
        icon={Crown}
        title="Plan Assigned"
        description={`Your partner has assigned you a subscription plan. ${
          hasPaymentMethod 
            ? 'Click "Activate Plan" to complete payment and start using your AI Employee.'
            : 'Add a payment method and activate it to start using your AI Employee.'
        }`}
        buttonText="Activate Plan"
        buttonIcon={Crown}
        onButtonClick={() =>navigate('/dashboard/billing?tab=subscription-plans')}
        hintText="Activate now"
      />
    );
  }

  // Priority 2: No payment method (show regardless of credit status)
  if (!hasPaymentMethod) {
    return (
      <StatusBanner
        variant="blue"
        icon={CreditCard}
        title="Recommended"
        description="Add a payment method to enable credits auto-refill and avoid service interruptions"
        buttonText="Add Payment Method"
        buttonIcon={Plus}
        onButtonClick={()=>navigate('/dashboard/billing?tab=payment-methods')}
        hintText="Optional but helpful"
      />
    );
  }

  // Priority 3: Delinquent account (has payment method but no credits)
  if (isDelinquent) {
    return (
      <StatusBanner
        variant="red"
        icon={AlertTriangle}
        title="Service Suspended"
        description="Your AI Employee needs credits to continue working for you"
        buttonText="Restore Service"
        buttonIcon={Zap}
        onButtonClick={() => navigate('/dashboard/billing?tab=boost-packs')}
        secondaryButtonText={autoRefillOff ? 'Turn on Auto-Refill' : undefined}
        secondaryButtonIcon={RefreshCw}
        onSecondaryButtonClick={autoRefillOff ? () => navigate('/dashboard/billing?tab=auto-refill') : undefined}
        hintText="2-minute fix"
      />
    );
  }

  // Priority 4: Low credits (has payment method, service active, but running low)
  if (isLowCredits) {
    return (
      <StatusBanner
        variant="amber"
        icon={CreditCard}
        title="Running Low"
        description={
          autoRefillOff
            ? `${billingUsage.creditsRemaining.toFixed(1)} credits remaining. Turn on Auto-Refill so your AI Employee never stops mid-day`
            : `${billingUsage.creditsRemaining.toFixed(1)} credits remaining. Add more to keep your AI Employee active`
        }
        buttonText="Add Credits"
        buttonIcon={CreditCard}
        onButtonClick={() => navigate('/dashboard/billing?tab=boost-packs')}
        secondaryButtonText={autoRefillOff ? 'Turn on Auto-Refill' : undefined}
        secondaryButtonIcon={RefreshCw}
        onSecondaryButtonClick={autoRefillOff ? () => navigate('/dashboard/billing?tab=auto-refill') : undefined}
        hintText="Prevent interruption"
      />
    );
  }

  // User has payment method and sufficient credits - no banner needed
  return null;
} 